import { useEffect, useState } from "react";

type TypewriterOptions = {
  /** When false the effect is idle and the output resets to "". */
  active?: boolean;
  /** Delay per character while typing (ms). */
  typeSpeed?: number;
  /** Delay per character while deleting (ms). */
  deleteSpeed?: number;
  /** Pause after a phrase is fully typed, before deleting (ms). */
  holdTime?: number;
  /** Pause after a phrase is fully deleted, before the next one (ms). */
  betweenTime?: number;
};

/**
 * Cycles through `phrases`, typing each one out a character at a time, pausing,
 * then backspacing it before moving to the next (looping forever). Returns the
 * currently visible substring. Driven by a single self-rescheduling timer so the
 * per-step delay can vary (type vs. delete vs. pause) without extra state.
 */
export function useTypewriter(
  phrases: string[],
  {
    active = true,
    typeSpeed = 55,
    deleteSpeed = 25,
    holdTime = 900,
    betweenTime = 200,
  }: TypewriterOptions = {},
): string {
  const [text, setText] = useState("");

  useEffect(() => {
    if (!active || phrases.length === 0) {
      setText("");
      return;
    }

    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const phrase = phrases[phraseIndex];
      charIndex += deleting ? -1 : 1;
      setText(phrase.slice(0, charIndex));

      let delay = deleting ? deleteSpeed : typeSpeed;
      if (!deleting && charIndex === phrase.length) {
        deleting = true;
        delay = holdTime;
      } else if (deleting && charIndex === 0) {
        deleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        delay = betweenTime;
      }
      timer = setTimeout(tick, delay);
    };

    timer = setTimeout(tick, betweenTime);
    return () => clearTimeout(timer);
    // `phrases` is treated as static; callers pass a stable/literal array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, typeSpeed, deleteSpeed, holdTime, betweenTime]);

  return text;
}
